import {
  IDynamicBindableValue,
  IDynamicItem,
  IDynamicSimpleText,
} from '@epicuro-next/models/platform/dynamic-elements/dynamic-elements';

import { conditionalMessage } from './misc';

export type LoadingStateWrapperProperties = {
  loadingStateWrapper: IDynamicBindableValue;
  isLoading: IDynamicBindableValue;
  hasError: IDynamicBindableValue;
};

export type DataLoadingProperties<T extends string> =
  LoadingStateWrapperProperties & {
    [K in T]: IDynamicBindableValue;
  };

export function hasErrorTransform(loadingStateWrapper: object) {
  if (typeof loadingStateWrapper !== 'object') {
    return false;
  }

  return '__resolveValueError__' in loadingStateWrapper;
}

export function isLoadingTransform(
  loadingStateWrapper: Pick<LoadingStateWrapper, 'isLoading'>,
): boolean {
  if (typeof loadingStateWrapper !== 'object') {
    return false;
  }

  const returnValue =
    !('__resolveValueError__' in loadingStateWrapper) &&
    loadingStateWrapper.isLoading !== false;

  return returnValue;
}

export interface LoadingStateWrapper<T = unknown> {
  isLoading?: boolean;
  data?: T;
}

export function referenceloadingStateWrapper(
  provider = 'self',
): IDynamicBindableValue {
  return {
    provider,
    property: 'loadingStateWrapper',
  };
}

export function isLoading(provider = 'self') {
  return {
    ...referenceloadingStateWrapper(provider),
    transform: isLoadingTransform.toString(),
  };
}

export function hasError(provider = 'self') {
  return {
    ...referenceloadingStateWrapper(provider),
    transform: hasErrorTransform.toString(),
  };
}

export function errorMessage(
  text = '@@anErrorOccurredWhileFetchingRecommendations',
  property = 'hasError',
): IDynamicSimpleText {
  return conditionalMessage('error-message', 'parent', property, text);
}

export function ifIsLoading(
  provider: string = 'parent',
  property: string = 'isLoading',
): Exclude<IDynamicItem['if'], undefined> {
  return ifLoading(provider, property, true);
}

export function ifIsNotLoading(
  provider: string = 'parent',
  property: string = 'isLoading',
): Exclude<IDynamicItem['if'], undefined> {
  return ifLoading(provider, property, false);
}

function ifLoading(
  provider: string,
  property: string,
  value: boolean,
): Exclude<IDynamicItem['if'], undefined> {
  return [
    'EQ',
    [
      {
        provider,
        property,
      },
      value,
    ],
  ];
}

export function ifHasError(
  provider: string = 'parent',
  property: string = 'hasError',
): Exclude<IDynamicItem['if'], undefined> {
  return [
    'EQ',
    [
      {
        provider,
        property,
      },
      true,
    ],
  ];
}

export function ifHasNoError(
  hasErrorprovider: IDynamicBindableValue = {
    provider: 'parent',
    property: 'hasError',
  },
): Exclude<IDynamicItem['if'], undefined> {
  return ['EQ', [hasErrorprovider, false]];
}

export function ifHasNoErrorAndIsNotLoading(
  provider: string = 'parent',
  isLoadingKey: string = 'isLoading',
  hasErrorKey: string = 'hasError',
): IDynamicItem['if'] {
  return [
    'AND',
    [
      ifHasNoError({ provider, property: hasErrorKey }),
      ifIsNotLoading(provider, isLoadingKey),
    ],
  ];
}
